import React from "react";

import { IPictoProps, NavItem, Picto } from "@amphore-dev/amphore-lib";
import { useNavigate } from "react-router-dom";

import { useIsRouteActive } from "@/hooks/useIsRouteActive";

import { TRoute } from "@/types";

export interface IMenuItemProps extends Omit<TRoute, "title"> {
	/** The address the entry links to (its `path`, or the first of them). */
	to: string;
	picto?: IPictoProps["icon"];
	isReduced?: boolean;
	children?: React.ReactNode;
}

/**
 * One sidebar entry: the lib's NavItem (router-agnostic, `<a href>`),
 * wired to react-router. Active on every path the route stands for, not
 * only its `to` - the home entry links to "/" but owns /calendar/:id too.
 * A plain click navigates in-app; modifier clicks keep the browser's own
 * open-in-new-tab.
 */
export const MenuItem: React.FC<IMenuItemProps> = ({
	to,
	path,
	children,
	picto,
	isReduced,
	locked,
}) => {
	const navigate = useNavigate();
	const isActive = useIsRouteActive(path ?? to);

	return (
		<NavItem
			href={to}
			isActive={isActive}
			isReduced={isReduced}
			picto={picto}
			className="w-full"
			onClick={(event) => {
				if (
					event.button !== 0 ||
					event.metaKey ||
					event.ctrlKey ||
					event.shiftKey ||
					event.altKey
				)
					return;
				event.preventDefault();
				navigate(to);
			}}
		>
			{locked ? (
				<>
					{children}
					<span
						className="ml-auto inline-flex opacity-60"
						data-testid="menu-lock"
					>
						<Picto icon="lock" className="h-3.5 w-3.5" />
					</span>
				</>
			) : (
				children
			)}
		</NavItem>
	);
};
